import { DATE_FORMAT_EU, DATE_FORMAT_US, formatDate } from "_utils/dateUtils";
import { useCookies } from "_utils/useCookies";
import { useExpandable } from "_utils/useExpandable";
import { netjetsCompanies } from "domain/company/constants";
import { useCompany } from "domain/company/useCompany";
import React, { createContext, useCallback, useContext, useEffect } from "react";

import { UserSettings } from "../userSettings";
import { UserSettingsModal } from "../userSettingsModal";

const columbusAirportCode = "KCMH";
const lisbonAirportCode = "LPPT";

const defaultUserSettingsUs: UserSettings = {
  isMetric: false,
  dateFormat: DATE_FORMAT_US,
  defaultAirportCode: columbusAirportCode,
  companyCode: netjetsCompanies.nja.companyCode,
};

const defaultUserSettingsEu: UserSettings = {
  isMetric: true,
  dateFormat: DATE_FORMAT_EU,
  defaultAirportCode: lisbonAirportCode,
  companyCode: netjetsCompanies.nje.companyCode,
};

interface UserSettingContext {
  readonly userSettings: UserSettings;
  readonly setUserSettings: (state: UserSettings) => void;
  readonly formatDate: (date: Date) => string;
  readonly openModal: () => void;
  readonly closeModal: () => void;
}

const userSettingsContext = createContext<UserSettingContext | undefined>(undefined);

export const UserSettingsProvider = ({ children }: UserSettingsProviderProps) => {
  const { cookies: storedUserSettings, storeCookies: storeUserSettings } = useCookies<UserSettings>();
  const { companyCodes, selectedCompanyCode, selectCompany } = useCompany();
  const isEurope = companyCodes.includes(netjetsCompanies.nje.companyCode);
  const defaultUserSettings = isEurope ? defaultUserSettingsEu : defaultUserSettingsUs;
  const userSettings: UserSettings = {
    ...defaultUserSettings,
    ...storedUserSettings,
  };

  const { open: openModal, close: closeModal, isOpen } = useExpandable();

  const setUserSettings = useCallback(
    (userSettings: UserSettings) => {
      selectCompany(userSettings.companyCode);
      storeUserSettings(userSettings);
    },
    [selectCompany, storeUserSettings],
  );

  useEffect(() => {
    if (!!selectedCompanyCode && selectedCompanyCode === storedUserSettings?.companyCode) {
      return;
    } else if (!!selectedCompanyCode && selectedCompanyCode !== storedUserSettings?.companyCode) {
      storeUserSettings({ ...storedUserSettings, companyCode: selectedCompanyCode });
    } else if (!!storedUserSettings?.companyCode && companyCodes.includes(storedUserSettings.companyCode)) {
      selectCompany(storedUserSettings.companyCode);
    } else if (!selectedCompanyCode && companyCodes.length > 1) {
      // make user select
      openModal();
    }
  }, [companyCodes, openModal, selectCompany, selectedCompanyCode, storeUserSettings, storedUserSettings]);

  const value = {
    userSettings,
    setUserSettings,
    formatDate: (date: Date) => formatDate(date, userSettings.dateFormat),
    openModal,
    closeModal,
  };

  return (
    <userSettingsContext.Provider value={value}>
      {children}
      <UserSettingsModal isOpen={isOpen} close={closeModal} />
    </userSettingsContext.Provider>
  );
};

interface UserSettingsProviderProps {
  children: React.ReactNode;
}

export const useUserSettings = () => {
  const context = useContext(userSettingsContext);
  if (context === undefined) {
    throw new Error("useUserSettings must be used within a UserSettingsProvider");
  }
  return context;
};
